import { useCallback, useRef, useState, type ReactNode } from "react";
import { useUncontrolledState } from "@tellescope/react-hooks";

import type { SelectProps } from "./Select";
import type { SelectContextValue, SelectItemRecord } from "./SelectContext";

const ignoreOpenChange = () => {};

export const useSelect = <TValue extends string>(props: SelectProps<TValue>) => {
  const { open, defaultOpen = false, onOpenChange, disabled = false, modal = false } = props;
  const [uncontrolledSingle, setUncontrolledSingle] = useState<TValue | undefined>(
    props.multiple ? undefined : props.defaultValue
  );
  const [uncontrolledMulti, setUncontrolledMulti] = useState<TValue[]>(props.multiple ? props.defaultValue ?? [] : []);
  // `setOpen` reports every change through `onOpenChange`, so the controlled setter has nothing left to do.
  const [isOpen, setIsOpen] = useUncontrolledState({
    state: open ?? defaultOpen,
    setState: open === undefined ? undefined : ignoreOpenChange,
  });

  // Presence, not the value, decides control: JSX `value={x}` always sets the key, so a controlled
  // Select whose `x` is undefined stays controlled and empty.
  const isControlled = "value" in props;
  const singleValue = props.multiple ? undefined : isControlled ? props.value : uncontrolledSingle;
  const selectedValues: TValue[] = props.multiple
    ? isControlled
      ? props.value ?? []
      : uncontrolledMulti
    : singleValue === undefined || singleValue === ""
    ? []
    : [singleValue];
  const maxCountReached = !!props.multiple && props.maxCount !== undefined && selectedValues.length >= props.maxCount;

  const records = useRef(new Map<string, SelectItemRecord>());
  const mountedCounts = useRef(new Map<string, number>());
  const [itemCount, setItemCount] = useState(0);
  const [, setRecordVersion] = useState(0);
  const suppressFocusOpen = useRef(false);
  const closedBySelection = useRef(false);

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) closedBySelection.current = false;
      setIsOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [setIsOpen, onOpenChange]
  );

  const setMultiValues = (nextValues: TValue[]) => {
    if (!props.multiple) return;
    setUncontrolledMulti(nextValues);
    props.onValueChange?.(nextValues);
  };

  const selectItem = (nextValue: TValue) => {
    if (props.multiple) {
      if (selectedValues.includes(nextValue)) {
        setMultiValues(selectedValues.filter((selected) => selected !== nextValue));
      } else if (!maxCountReached) {
        setMultiValues([...selectedValues, nextValue]);
      }
      return;
    }
    setUncontrolledSingle(nextValue);
    props.onValueChange?.(nextValue);
    closedBySelection.current = true;
    setOpen(false);
  };

  const removeValue = (valueToRemove: TValue) => {
    setMultiValues(selectedValues.filter((selected) => selected !== valueToRemove));
  };

  const clear = () => {
    if (props.multiple) {
      setMultiValues([]);
      return;
    }
    setUncontrolledSingle(undefined);
    if (props.clearable) props.onValueChange?.(undefined);
  };

  const registerItem = useCallback((itemValue: string) => {
    mountedCounts.current.set(itemValue, (mountedCounts.current.get(itemValue) ?? 0) + 1);
    setItemCount(mountedCounts.current.size);
    return () => {
      const remaining = (mountedCounts.current.get(itemValue) ?? 1) - 1;
      if (remaining > 0) mountedCounts.current.set(itemValue, remaining);
      else mountedCounts.current.delete(itemValue);
      setItemCount(mountedCounts.current.size);
    };
  }, []);

  // Records outlive unmounting on purpose: with external filtering the selected item drops out of
  // the results, and the trigger should keep showing the label it had when it was last rendered.
  // Records are written after the trigger renders, so a changed label (e.g. a renamed user) needs a
  // re-render to reach it. Compared by text because the node is a fresh element every render.
  const rememberItem = useCallback((itemValue: string, record: SelectItemRecord) => {
    const previous = records.current.get(itemValue);
    const changed = previous?.textValue !== record.textValue || previous?.disabled !== record.disabled;
    records.current.set(itemValue, record);
    if (changed) setRecordVersion((version) => version + 1);
  }, []);

  const getItem = useCallback((itemValue: string) => records.current.get(itemValue), []);

  const isRendered = useCallback((itemValue: string) => mountedCounts.current.has(itemValue), []);

  const hasItemMatching = useCallback((text: string) => {
    const lowerText = text.toLowerCase();
    return Array.from(mountedCounts.current.keys()).some(
      (itemValue) =>
        itemValue.toLowerCase() === lowerText || records.current.get(itemValue)?.textValue.toLowerCase() === lowerText
    );
  }, []);

  // Method syntax accepts the `TValue`-typed callback where the context's string signature is expected.
  const labels: { fallback?(value: string): ReactNode } = { fallback: props.fallbackLabel };

  const context: SelectContextValue = {
    multiple: !!props.multiple,
    value: props.multiple ? undefined : singleValue,
    selectedValues,
    isSelected: (itemValue) => selectedValues.some((selected) => selected === itemValue),
    selectItem,
    removeValue,
    fallbackLabel: props.fallbackLabel,
    creator: props.creator,
    labelFor: (itemValue) => records.current.get(itemValue)?.node ?? labels.fallback?.(itemValue) ?? itemValue,
    maxCountReached,
    maxDisplayCount: props.multiple ? props.maxDisplayCount : undefined,
    open: isOpen,
    setOpen,
    disabled,
    modal,
    clearable: !!props.clearable,
    clear,
    itemCount,
    registerItem,
    rememberItem,
    getItem,
    isRendered,
    hasItemMatching,
    suppressFocusOpen,
    closedBySelection,
  };

  return { context, isOpen, setOpen, modal };
};
